/**
 * Hook for handling template drop from Add Element modal
 */
import { useCallback } from "react"
import { notify } from "shared/ui/Notifications/notify"
import { Editor as TiptapEditor } from "@tiptap/react"
import { EditorRef } from "types/questionnaire.types"
import { StructureNode } from "utils/structureParser"
import { htmlToText, textToHtml } from "utils/htmlTextConverter"
import { findNodeByKey } from "utils/structureTreeHelpers"
import actions from "features/QuestionnaireEditor/components/Editor/MenuBar/MenuActions"

interface UseTemplateDropParams {
  editor: TiptapEditor | null
  editorRef: React.RefObject<EditorRef | null>
  structureTree: StructureNode | null
  parseStructure: () => void
  scrollToLine?: (lineNumber: number) => void
}

interface UseTemplateDropReturn {
  handleTemplateDrop: (
    e: React.DragEvent,
    targetNodeKey: React.Key,
    position: "before" | "after" | "inside"
  ) => void
}

/**
 * Custom hook to handle template drag-and-drop from Add Element modal
 */
export const useTemplateDrop = ({
  editor,
  editorRef,
  structureTree,
  parseStructure,
  scrollToLine,
}: UseTemplateDropParams): UseTemplateDropReturn => {

  const handleTemplateDrop = useCallback(
    (e: React.DragEvent, targetNodeKey: React.Key, position: "before" | "after" | "inside") => {
      e.preventDefault()
      e.stopPropagation()

      const templateKey = e.dataTransfer.getData("application/template")
      if (!templateKey || !editor || !editorRef.current || !structureTree) {
        return
      }

      // Get the template content
      const actionMap = actions(editor)
      const action = actionMap[templateKey as keyof typeof actionMap]
      if (!action || typeof action !== "function") {
        notify.toast.error("Invalid template")
        return
      }

      // Find the target node
      const targetNode = findNodeByKey(structureTree, String(targetNodeKey))
      if (!targetNode || targetNode.lineNumber <= 0) {
        notify.toast.error("Invalid drop target")
        return
      }

      // Get current editor content
      const editorHTML = editorRef.current.getHTML()
      const plainText = htmlToText(editorHTML)
      const lines = plainText.split("\n")

      // Get the template content by creating a mock editor
      let templateHTML = ""
      const mockEditor = {
        commands: {
          insertContent: (c: string) => {
            templateHTML = c
          }
        }
      }
      const actionFunc = actions(mockEditor as TiptapEditor)[templateKey as keyof typeof actionMap]

      if (typeof actionFunc === "function") {
        actionFunc()
      }

      const templateText = htmlToText(templateHTML)

      // Calculate insertion position based on drop position
      let insertLine: number
      if (position === "before") {
        // Insert before the target node
        insertLine = targetNode.lineNumber - 1
      } else if (position === "after") {
        // Insert after the target node's end
        insertLine = targetNode.endLineNumber || targetNode.lineNumber
      } else {
        // Insert inside (as first child) - after the target line
        insertLine = targetNode.lineNumber
      }

      const newLines = [
        ...lines.slice(0, insertLine),
        templateText,
        ...lines.slice(insertLine),
      ]

      // Update editor
      const newContent = newLines.join("\n")
      const newHTML = textToHtml(newContent)
      editorRef.current.setHTML(newHTML)

      // Scroll to the inserted line
      const scrollToInsertedLine = insertLine + 1 // +1 because line numbers start at 1, not 0
      if (scrollToLine) {
        setTimeout(() => {
          scrollToLine(scrollToInsertedLine)
        }, 150)
      }

      // Reparse structure
      setTimeout(() => {
        parseStructure()
      }, 100)

      notify.toast.success("Template inserted successfully")
    },
    [editor, editorRef, structureTree, parseStructure, scrollToLine]
  )

  return {
    handleTemplateDrop,
  }
}
